/**
 * Phase 4 — Public-access probe types and the closed reason-code enum.
 *
 * Per the historical hybrid-proposal planning brief (§7.8; preserved in
 * git history — see docs/archived-components.md) the probe outcomes are
 * a closed enum that maps 1:1 to user-facing report wording. Anything
 * that is not literally `verified-public` must surface a cause-specific
 * message — never a generic "item is private."
 *
 * This module lives under src/client/migration so the worker bundle can
 * import it without dragging in any Node-only code. It contains data
 * definitions only (no DOM, no fetch, no globals) so it is also safe to
 * unit-test in plain Node.
 */

/**
 * Closed enum of probe outcomes. Order matches the table in
 * hybrid-proposal-strategy.md §7.8.
 */
export type ProbeOutcome =
  | 'verified-public'
  | 'requires-authentication'
  | 'not-found'
  | 'mixed-content-blocked'
  | 'internal-or-unroutable-host'
  | 'network-or-cors-blocked'
  | 'timeout'
  | 'unexpected-response'
  | 'probe-disabled';

/**
 * Mapping from each ProbeOutcome to the verbatim user-facing wording
 * required by the proposal. Consumers (HTML report, PDF, DOCX) MUST
 * source the wording from here so the three formats stay in sync and
 * so the security-adjacent wording cannot drift accidentally.
 */
export const PROBE_OUTCOME_WORDING: Readonly<Record<ProbeOutcome, string>> = Object.freeze({
  'verified-public':
    'Verified anonymously reachable. Velocity should be able to read this item.',
  'requires-authentication':
    'Item exists but is not anonymously readable (access is restricted to specific users, groups, or your organization). Migration requires manual confirmation that the Velocity service account has access.',
  'not-found':
    'Item could not be found at the URL recorded in the GeoEvent configuration. The item may have been deleted, renamed, or moved.',
  'mixed-content-blocked':
    'Could not check this URL from your browser because it uses HTTP while the assistant is served over HTTPS. Browsers block mixed-content requests. Verify the item\u2019s access settings manually, or update the source URL to HTTPS.',
  'internal-or-unroutable-host':
    'Could not check this URL from your browser because the host appears to be on an internal network (for example, an ArcGIS Server virtual directory). Verify manually that the Velocity service account can reach this host and that the item is anonymously readable.',
  'network-or-cors-blocked':
    'Could not reach this URL from your browser. The server may not allow cross-origin requests, may be unreachable from your network, or may be temporarily offline. This is not a determination that the item is private.',
  'timeout':
    'Check timed out. The server did not respond within the allotted time. This is not a determination that the item is private.',
  'unexpected-response':
    'Received an unexpected response when checking this URL. Verify the item manually.',
  'probe-disabled':
    'Public-access check was not performed for this item.',
});

/**
 * A single probe result. Tied to the URL it was issued against so the
 * worker can attach probe outcomes back to the parsed component(s) that
 * reference that URL.
 */
export interface ProbeResult {
  /** The exact URL string that was classified or fetched. */
  url: string;
  /** Closed-enum outcome. */
  outcome: ProbeOutcome;
}

/**
 * Pre-flight classifier verdict. When `outcome` is set, the URL must NOT
 * be sent over the network — the worker assigns the outcome directly.
 * When `outcome` is undefined, the worker should proceed to the network
 * probe.
 */
export interface PreflightClassification {
  outcome?: ProbeOutcome;
  /**
   * Parsed URL when classification succeeded. Undefined when the URL
   * failed to parse (in which case `outcome` is `unexpected-response`).
   */
  parsed?: URL;
}

/**
 * Hostname patterns that indicate an internal/unroutable target. Browser
 * cannot reach these from outside the customer network and the proposal
 * requires returning `internal-or-unroutable-host` without issuing the
 * request (privacy + clearer reason code).
 *
 * Exported for unit-test introspection only.
 */
export const INTERNAL_HOST_SUFFIXES: readonly string[] = Object.freeze([
  '.local',
  '.internal',
  '.lan',
  '.intranet',
  '.corp',
  '.home.arpa',
]);

/** Loopback hostnames. */
export const LOOPBACK_HOSTNAMES: readonly string[] = Object.freeze([
  'localhost',
  'ip6-localhost',
  'ip6-loopback',
]);

/**
 * Returns true when the hostname has no dots (single-label host). Single-
 * label hosts are only resolvable on the local search-domain network and
 * are treated as internal per the proposal.
 */
function isSingleLabelHost(hostname: string): boolean {
  return hostname.length > 0 && !hostname.includes('.') && !hostname.includes(':');
}

/**
 * Returns true when the hostname is an IPv4 address literal in a
 * private/loopback/link-local/CGNAT range.
 *
 * Covered: RFC1918 (10/8, 172.16/12, 192.168/16), loopback (127/8),
 * link-local (169.254/16), CGNAT (100.64/10), broadcast (255.255.255.255),
 * unspecified (0.0.0.0).
 */
function isPrivateIPv4(hostname: string): boolean {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname);
  if (!match) return false;
  const a = Number(match[1]);
  const b = Number(match[2]);
  const c = Number(match[3]);
  const d = Number(match[4]);
  if (![a, b, c, d].every((n) => n >= 0 && n <= 255)) return false;
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 0) return true;
  if (a === 169 && b === 254) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a === 255 && b === 255 && c === 255 && d === 255) return true;
  return false;
}

/**
 * Returns true when the hostname is an IPv6 address literal in a
 * loopback (::1), unspecified (::), link-local (fe80::/10), or unique
 * local (fc00::/7, covers RFC4193) range. URL.hostname strips the
 * surrounding `[]` brackets used in URI syntax.
 */
function isPrivateIPv6(hostname: string): boolean {
  // URL.hostname returns IPv6 literals wrapped in [] in browsers; strip
  // so the prefix checks below work uniformly.
  const stripped = hostname.startsWith('[') && hostname.endsWith(']')
    ? hostname.slice(1, -1)
    : hostname;
  if (!stripped.includes(':')) return false;
  const lower = stripped.toLowerCase();
  if (lower === '::1' || lower === '::') return true;
  if (lower.startsWith('fe8') || lower.startsWith('fe9') || lower.startsWith('fea') || lower.startsWith('feb')) {
    return true; // fe80::/10
  }
  // fc00::/7 covers fc.. and fd..
  if (lower.startsWith('fc') || lower.startsWith('fd')) return true;
  return false;
}

/**
 * Returns true when the hostname matches any of the known internal
 * suffixes (case-insensitive). The suffix list is in
 * INTERNAL_HOST_SUFFIXES.
 */
function hasInternalSuffix(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  return INTERNAL_HOST_SUFFIXES.some((suffix) => lower.endsWith(suffix));
}

/**
 * Returns true when the hostname is a loopback name (case-insensitive).
 */
function isLoopbackName(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  return LOOPBACK_HOSTNAMES.includes(lower);
}

/**
 * Heuristic test for "internal or unroutable" hosts per §7.8.
 * Aggregates the IP-literal checks, the suffix list, the single-label
 * test, and the loopback name list.
 *
 * Exported for direct unit-testing.
 */
export function isInternalOrUnroutableHost(hostname: string): boolean {
  if (!hostname) return true;
  if (isLoopbackName(hostname)) return true;
  if (isPrivateIPv4(hostname)) return true;
  if (isPrivateIPv6(hostname)) return true;
  if (hasInternalSuffix(hostname)) return true;
  if (isSingleLabelHost(hostname)) return true;
  return false;
}

/**
 * Inputs for the pre-flight classifier. `appProtocol` lets tests
 * simulate an HTTPS-served assistant without depending on `window`.
 * Callers in the worker pass `self.location?.protocol` (or `'https:'`
 * by default when the assistant is served over HTTPS).
 */
export interface PreflightInputs {
  url: string;
  /** The protocol the assistant page itself was served over. */
  appProtocol: 'http:' | 'https:';
}

/**
 * Pre-flight URL inspection (no network call) per §7.8. Returns either:
 *   - { outcome: <terminal> }            when the URL can be classified
 *     without issuing a request (mixed content, internal host, malformed
 *     URL).
 *   - { outcome: undefined, parsed: URL } when the URL passed all
 *     pre-flight checks and the network probe should proceed.
 */
export function preflightClassifyUrl(inputs: PreflightInputs): PreflightClassification {
  let parsed: URL;
  try {
    parsed = new URL(inputs.url);
  } catch {
    return { outcome: 'unexpected-response' };
  }

  // Only http(s) targets are probe candidates. Anything else (file:,
  // ftp:, data:, javascript:, ...) is treated as unexpected; the
  // proposal does not enumerate a code for these and unexpected is the
  // safe under-commit.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { outcome: 'unexpected-response', parsed };
  }

  if (inputs.appProtocol === 'https:' && parsed.protocol === 'http:') {
    return { outcome: 'mixed-content-blocked', parsed };
  }

  if (isInternalOrUnroutableHost(parsed.hostname)) {
    return { outcome: 'internal-or-unroutable-host', parsed };
  }

  return { parsed };
}

/**
 * Returns the verbatim user-facing message for an outcome. Thin wrapper
 * around PROBE_OUTCOME_WORDING that exists so render code never reaches
 * into the table directly (eases future i18n).
 */
export function probeOutcomeMessage(outcome: ProbeOutcome): string {
  return PROBE_OUTCOME_WORDING[outcome];
}
